/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */

const { default: PageLoadService } = require('./src/Services/PageLoadService');
const { default: UtilsService } = require('./src/Services/UtilsService');
const {
    default: AnalyticsService,
    AnalyticsEvents
} = require('./src/Services/AnalyticsService');
const { default: IntercomService } = require('./src/Services/IntercomService');
const { initGoogleOneTap } = require('./src/google-one-tap/googleOneTap');
const {
    ABTestToolUtils: ABTestToolUtilsV2
} = require('./src/Services/ABTestToolServiceUtils.v2');
const {
    EXPECTED_ERROR_NOISE,
    EXTENSION_STACK_NOISE
} = require('./src/constants/errors');
const {
    ABTestContextProvider
} = require('./src/ab-tests/context/ABTestContext.provider');
const { LocationProvider } = require('@gatsbyjs/reach-router');
const React = require('react');
const {
    default: DiscountBanner
} = require('./src/components/DiscountBanner/DiscountBanner');
const isProduction = process.env.GATSBY_env === 'production';
const isStaging = UtilsService.isStaging();
const { CloudinaryContext } = require('cloudinary-react');

function loadMta() {
    const rf = encodeURIComponent(document.referrer);
    const img = new Image(); // width, height values are optional params

    img.referrerPolicy = 'unsafe-url';
    let pixel_url;

    if (isProduction) {
        pixel_url = 'https://pixel.honeybook.com/mta';
    } else {
        pixel_url = 'https://pixel-dev.honeybook.com/mta';
    }

    img.src = pixel_url + (rf ? `?rf=${rf}` : '');
}

function loadGtm() {
    if (['development'].includes(process.env.GATSBY_env)) return;
    (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js'
        });
        var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
        j.defer = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-5CZT6X');
}

function loadBeacon() {
    (function () {
        if (!isProduction) return;
        try {
            var c = atob('aG9uZXlib29rLmNvbQ==');
            if (!('.' + document.location.hostname).endsWith('.' + c)) {
                new Image().src =
                    'h' +
                    'ttp' +
                    's:' +
                    atob('Ly9mb250bWU=') +
                    atob('bi5zY2lydA==').split('').reverse().join('') +
                    'et/' +
                    c +
                    '.p' +
                    'ng?u=' +
                    escape(document.location) +
                    '&r=' +
                    escape(document.referrer) +
                    '&ra=' +
                    Math.random();
            }
        } catch (e) {}
    })();
}

function trackSearchReferrals() {
    const referrerUrl = document.referrer;
    if (!referrerUrl) {
        return;
    }

    const searchEngineUrls = [
        { name: 'Google', url: 'google.com' },
        { name: 'Bing', url: 'bing.com' }
    ];

    const referringSearchEngine = searchEngineUrls.find(engine =>
        referrerUrl.includes(engine.url)
    );

    if (referrerUrl && referringSearchEngine) {
        AnalyticsService.reportEvent(
            `Page View from Search - ${referringSearchEngine.name}`
        );
    }
}

function loadSegment() {
    if (process.env.GATSBY_SegmentKey) {
        !(function () {
            var analytics = (window.analytics = window.analytics || []);
            if (!analytics.initialize)
                if (analytics.invoked)
                    window.console &&
                        console.error &&
                        console.error('Segment snippet included twice.');
                else {
                    analytics.invoked = !0;
                    analytics.methods = [
                        'trackSubmit',
                        'trackClick',
                        'trackLink',
                        'trackForm',
                        'pageview',
                        'identify',
                        'reset',
                        'group',
                        'track',
                        'ready',
                        'alias',
                        'debug',
                        'page',
                        'once',
                        'off',
                        'on',
                        'addSourceMiddleware',
                        'addIntegrationMiddleware',
                        'setAnonymousId',
                        'addDestinationMiddleware'
                    ];
                    analytics.factory = function (e) {
                        return function () {
                            var t = Array.prototype.slice.call(arguments);
                            t.unshift(e);
                            analytics.push(t);
                            return analytics;
                        };
                    };
                    for (var e = 0; e < analytics.methods.length; e++) {
                        var key = analytics.methods[e];
                        analytics[key] = analytics.factory(key);
                    }
                    analytics.load = function (key, e) {
                        var t = document.createElement('script');
                        t.type = 'text/javascript';
                        t.async = !0;
                        t.src =
                            'https://hscm.honeybook.com/analytics.js/v1/' +
                            key +
                            '/analytics.min.js';
                        var n = document.getElementsByTagName('script')[0];
                        n.parentNode.insertBefore(t, n);
                        analytics._loadOptions = e;
                    };
                    analytics._writeKey = process.env.GATSBY_SegmentKey;
                    analytics.SNIPPET_VERSION = '4.15.3';
                    analytics.load(process.env.GATSBY_SegmentKey, {
                        integrations: {
                            'Segment.io': {
                                deliveryStrategy: {
                                    config: { keepalive: true }
                                },
                                apiHost: 'hsam.honeybook.com/v1'
                            }
                        }
                    });
                }
        })();
    }
}
/* eslint-enable */

// Drop non-actionable browser/extension error noise before RUM records it.
// Noise lists live in src/constants/errors.ts (shared shape with hb-webflow).
function isExpectedErrorNoise(event) {
    if (!event || event.type !== 'error') {
        return false;
    }
    var error = event.error || {};
    if (
        typeof error.message === 'string' &&
        EXPECTED_ERROR_NOISE.some(function (noise) {
            return error.message.includes(noise);
        })
    ) {
        return true;
    }
    return (
        typeof error.stack === 'string' &&
        EXTENSION_STACK_NOISE.some(function (noise) {
            return error.stack.includes(noise);
        })
    );
}

function loadDataDog() {
    if (isProduction) {
        (function (h, o, u, n, d) {
            h = h[d] = h[d] || {
                q: [],
                onReady: function (c) {
                    h.q.push(c);
                }
            };
            d = o.createElement(u);
            d.async = 1;
            d.src = n;
            n = o.getElementsByTagName(u)[0];
            n.parentNode.insertBefore(d, n);
        })(
            window,
            document,
            'script',
            'https://www.datadoghq-browser-agent.com/datadog-rum-slim-v5.js',
            'DD_RUM'
        );
        DD_RUM.onReady(function () {
            DD_RUM.init({
                clientToken: process.env.GATSBY_datadog_clientToken,
                applicationId: process.env.GATSBY_datadog_applicationId,
                site: 'datadoghq.com',
                service: 'gatsby-marketing-site',
                env: 'prod',
                sampleRate: 1,
                trackInteractions: true,
                defaultPrivacyLevel: 'mask-user-input',
                allowedTracingOrigins: ['https://www.honeybook.com'],
                beforeSend: function (event) {
                    return !isExpectedErrorNoise(event);
                }
            });
        });
    }
}

// You can delete this file if you're not using it
exports.onInitialClientRender = () => {
    loadDataDog();
    loadSegment();
    if (PageLoadService.isUserLoggedIn) return;
    loadMta();
    loadGtm();
    initGoogleOneTap();
    IntercomService.handleIntercom();
    loadBeacon();
};

exports.onClientEntry = () => {
    AnalyticsService.handleHBAnonymousId();
    if (isStaging) {
        return;
    }
    trackSearchReferrals();
    if (!window.___loader) {
        return;
    }

    UtilsService.validateReferrer();
    ABTestToolUtilsV2.handleFallbackVariants();
};

exports.disableCorePrefetching = () => true;

exports.wrapRootElement = args => {
    return (
        <LocationProvider>
            <CloudinaryContext cloudName="honeybook" secure>
                <ABTestContextProvider>{args.element}</ABTestContextProvider>
            </CloudinaryContext>
        </LocationProvider>
    );
};

exports.wrapPageElement = ({ element, props }) => {
    const skipDiscountBanner = props.pageContext?.noPromo === true;

    return React.cloneElement(
        element,
        element.props,
        React.createElement(
            React.Fragment,
            null,
            !skipDiscountBanner &&
                React.createElement(DiscountBanner, { path: props.path }),
            element.props.children
        )
    );
};
